import { callAct, isOk, statusText } from "./apiConfig";
import utils from "./utils";

// Tables service — unified onto the PHP backend (acts: tablesList/addTable/editTable/
// delTable, tableShapeTypeList). PHP uses different field names than this client, so
// map both directions.
//   PHP row:   { ID, tableName, shapeType, defaultXSize, defaultYSize, xPosition,
//                yPosition, dinersNumber, tableAreaID, isPermanent }
//   client:    { id, name, shape, x, y, diners, area_id, is_locked }

function ensureOk(envelope, fallbackMsg) {
    if (!isOk(envelope)) throw new Error(statusText(envelope) || fallbackMsg);
    return envelope;
}

// The client renderer (CanvasTableMap) uses STRING shapes; PHP uses numeric
// shapeType ids from the tableShapeType table. The original PHP data defines only
// two: 1=rectangle (מלבן), 2=square (ריבוע). circle/triangle have NO PHP shape id
// (would need adding to the PHP tableShapeType table) — mapped best-effort on save.
const SHAPE_ID_TO_STR = { '1': 'rectangle', '2': 'square' };
const SHAPE_STR_TO_ID = { rectangle: 1, square: 2, circle: 2, triangle: 1 };

function phpToTable(t) {
    return {
        id: t.ID,
        name: t.tableName,
        area_id: t.tableAreaID,
        shape: SHAPE_ID_TO_STR[String(t.shapeType)] || 'square',
        shapeTypeId: t.shapeType, // keep the raw PHP id so edit can round-trip
        x: Number(t.xPosition),
        y: Number(t.yPosition),
        diners: Number(t.dinersNumber),
        is_locked: Number(t.isPermanent),
        defaultXSize: t.defaultXSize,
        defaultYSize: t.defaultYSize,
        // Per-table hold/occupancy time (זמן תפוסה). DB NULL => no override; keep it
        // null/undefined here (do NOT coerce to 0) so the seating engine falls back to the
        // business default. A stored value comes back as a positive number.
        holdTime: (t.tableHoldTimeInMinutes === null || t.tableHoldTimeInMinutes === undefined || t.tableHoldTimeInMinutes === '')
            ? null
            : Number(t.tableHoldTimeInMinutes),
    };
}

function tableToPhp(t) {
    const shapeType = SHAPE_STR_TO_ID[t.shape] ?? (t.shapeTypeId ? Number(t.shapeTypeId) : 2);
    return {
        tableName: t.name,
        shapeType,
        defaultXSize: t.defaultXSize ?? 0,
        defaultYSize: t.defaultYSize ?? 0,
        xPosition: t.x ?? 0,
        yPosition: t.y ?? 0,
        dinersNumber: t.diners ?? 0,
        tableAreaID: t.area_id,
        isPermanent: t.is_locked ? 1 : 0,
        // Per-table hold time (optional). Send a positive int to override; else 0 so the
        // backend stores NULL (= use the business default). 0 is the "clear" sentinel (the
        // 'int' input validator + the tableHoldTimeSqlLiteral() helper both treat <=0 as NULL).
        tableHoldTimeInMinutes: (Number(t.holdTime) > 0) ? Number(t.holdTime) : 0,
    };
}

function _isTableSaved() {
    const isTableList = utils.isSessionValid('table-list');
    return isTableList || false;
}
function _isShapeSaved() {
    const isShapeList = utils.isSessionValid('shape-list');
    return isShapeList || false;
}

function getTableById(id) {
    const tableList = _isTableSaved();
    if (tableList) {
        const table = tableList.find((t) => t.id === id);
        return table || [];
    }
    return [];
}

async function getShapes() {
    const cached = _isShapeSaved();
    if (cached) return cached;
    try {
        const envelope = ensureOk(await callAct('tableShapeTypeList', {}), 'Failed to load shapes');
        const shapes = (Array.isArray(envelope?.data) ? envelope.data : [])
            .map((s) => ({ id: s.ID, name: s.typeName, type: s.typeName }));
        utils.saveSession('shape-list', JSON.stringify(shapes));
        return shapes;
    } catch (error) {
        console.error(error);
        return [];
    }
}

async function getAllTables(isChange = false) {
    const cached = _isTableSaved();
    if (cached && !isChange) return cached;
    try {
        const envelope = ensureOk(await callAct('tablesList', {}), 'Failed to load tables');
        const tables = (Array.isArray(envelope?.data) ? envelope.data : []).map(phpToTable);
        utils.saveSession('table-list', JSON.stringify(tables));
        return tables;
    } catch (error) {
        console.error(error);
        return [];
    }
}

async function addTable(tableData) {
    try {
        return ensureOk(await callAct('addTable', tableToPhp(tableData)), 'Failed to add table');
    } catch (error) {
        console.error(error);
        return null; // null (not []) so callers testing truthiness see failure
    }
}

async function updateTable(table) {
    try {
        return ensureOk(await callAct('editTable', { ID: table.id, ...tableToPhp(table) }), 'Failed to update table');
    } catch (error) {
        console.error('Failed to update table:', error);
        return null;
    }
}

async function deleteTable(id) {
    try {
        const envelope = ensureOk(await callAct('delTable', { ID: id }), 'Failed to delete table');
        utils.clearSession('table-list');
        return envelope;
    } catch (error) {
        console.error(error);
        return null;
    }
}

export default { getAllTables, deleteTable, addTable, updateTable, getTableById, getShapes };
